import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { postData, updateData, fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';

const ItemModal = ({
  open,
  onClose,
  onSave,
  itemData = null,
  mode = 'create', // 'create', 'edit', 'view'
  categories = [],
  brands = [],
  uoms = [],
  existingItems = [],
  vendors = [],
  banks = []
}) => {
  const { companyId, userId, token, username } = useAuth();
  const { logAudit } = useAuditLog();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    Name: '',
    BarCode: '',
    Code: '',
    HSNCode: '',
    CategoryId: '',
    BrandId: '',
    UOMId: '',
    GSTRateId: '',
    GSTRate: '',
    MRP: '',
    PurchaseRate: '',
    MOP1: '',
    MOP2: '',
    MOP3: '',
    MOP4: '',
    MOP5: '',
    DealersPrice: '',
    MinRequiredQty: '',
    AlertLevelStock: '',
    IsProduct: false,
    IsReturnable: false,
    ReturnDays: '',
    HasWarranty: false,
    WarrantyDays: '',
    IsBundle: false,
    IsTradingItem: true,
    IsService: false,
    IsRawMaterial: false,
    IsInstallation: false,
    BankId: '',
    SubCategoryId: '',
    StarRating: 0
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Supplier/Vendor section state
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [itemVendors, setItemVendors] = useState([]);
  const [vendorDropdownKey, setVendorDropdownKey] = useState(0);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorDeleteConfirm, setVendorDeleteConfirm] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [vendorActivateConfirm, setVendorActivateConfirm] = useState(false);
  const [vendorToActivate, setVendorToActivate] = useState(null);
  const [inactiveVendors, setInactiveVendors] = useState([]);
  const [inactiveVendorLoading, setInactiveVendorLoading] = useState(false);

  // Branch-wise opening balance state
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [apiOpeningBalances, setApiOpeningBalances] = useState(null);

  // Bundle Items state
  const [bundleItems, setBundleItems] = useState([]);
  const [selectedBundleItem, setSelectedBundleItem] = useState(null);
  const [bundleQuantity, setBundleQuantity] = useState('');
  const [bundleDropdownKey, setBundleDropdownKey] = useState(0);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [bundleDeleteConfirm, setBundleDeleteConfirm] = useState(false);
  const [bundleToDelete, setBundleToDelete] = useState(null);

  // Production Items state
  const [productionItems, setProductionItems] = useState([]);
  const [selectedProductionItem, setSelectedProductionItem] = useState(null);
  const [productionQuantity, setProductionQuantity] = useState('');
  const [productionRate, setProductionRate] = useState('');
  const [productionDropdownKey, setProductionDropdownKey] = useState(0);
  const [productionLoading, setProductionLoading] = useState(false);
  const [productionDeleteConfirm, setProductionDeleteConfirm] = useState(false);
  const [productionToDelete, setProductionToDelete] = useState(null);

  // GST Rate dropdown state
  const [gstRates, setGstRates] = useState([]);
  const [gstRatesLoading, setGstRatesLoading] = useState(false);

  // SubCategory dropdown state
  const [subCategories, setSubCategories] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);

  // Item Offers & Warranty state
  const [offersAndWarranty, setOffersAndWarranty] = useState([]);
  const [newOfferText, setNewOfferText] = useState('');
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersExpanded, setOffersExpanded] = useState(true);
  const [offerDeleteConfirm, setOfferDeleteConfirm] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);

  // Item Specs state
  const [itemSpecs, setItemSpecs] = useState([]);
  const [newSpecText, setNewSpecText] = useState('');
  const [specsLoading, setSpecsLoading] = useState(false);
  const [specsExpanded, setSpecsExpanded] = useState(true);
  const [specDeleteConfirm, setSpecDeleteConfirm] = useState(false);
  const [specToDelete, setSpecToDelete] = useState(null);

  // Section expand/collapse state
  const [openingBalanceExpanded, setOpeningBalanceExpanded] = useState(false);
  const [vendorsExpanded, setVendorsExpanded] = useState(false);
  const [bundleExpanded, setBundleExpanded] = useState(true);
  const [productionExpanded, setProductionExpanded] = useState(true);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch GST Rates from API
  useEffect(() => {
    if (open && companyId && token) {
      fetchGSTRates();
      fetchSubCategoriesAll();
    }
  }, [open, companyId, token]);

  const fetchGSTRates = async () => {
    setGstRatesLoading(true);
    try {
      const response = await fetchData('GSTRateApi', `/${companyId}`, token);
      if (Array.isArray(response.response_data)) {
        const data = response.response_data;
        setGstRates(data.filter(r => r.IsActive !== false));
      } else {
        setGstRates([]);
      }
    } catch (error) {
      console.error('Error fetching GST rates:', error);
      setGstRates([]);
    } finally {
      setGstRatesLoading(false);
    }
  };

  const fetchSubCategoriesAll = async () => {
    setSubCategoriesLoading(true);
    try {
      const response = await fetchData('SubCategoriesApi', `/${companyId}`, token);
      const data = response?.response_data || response || [];
      setSubCategories(Array.isArray(data) ? data.filter(s => s.IsActive !== false) : []);
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      setSubCategories([]);
    } finally {
      setSubCategoriesLoading(false);
    }
  };

  // Track which (open+itemData) pair has already been initialised so that
  // late-arriving categories/brands/uoms updates don't overwrite user edits.
  const initializedKeyRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Build a key that uniquely identifies this modal session.
      // If this session was already initialised, only re-run when open/itemData/mode change.
      const sessionKey = `${open}-${itemData?.Id ?? 'new'}-${mode}`;
      const isNewSession = initializedKeyRef.current !== sessionKey;

      console.log('Modal opened with mode:', mode, 'itemData:', itemData);

      if (isNewSession) {
        fetchBranches();
        initializedKeyRef.current = sessionKey;
      }

      if (itemData && mode !== 'create') {
        console.log('Loading existing item data for edit/view, ID:', itemData.Id);
        console.log('CategoryId:', itemData.CategoryId, 'BrandId:', itemData.BrandId, 'UOMId:', itemData.UOMId);

        // If CategoryId is not present but CategoryName is, find the ID from categories array
        let categoryId = itemData.CategoryId || '';
        if (!categoryId && itemData.CategoryName) {
          const matchedCategory = categories.find(cat => cat.Name === itemData.CategoryName);
          if (matchedCategory) {
            categoryId = matchedCategory.Id;
            console.log('Matched CategoryName to CategoryId:', categoryId);
          }
        }

        // If BrandId is not present but BrandName is, find the ID from brands array
        let brandId = itemData.BrandId || '';
        if (!brandId && itemData.BrandName) {
          const matchedBrand = brands.find(brand => brand.Name === itemData.BrandName);
          if (matchedBrand) {
            brandId = matchedBrand.Id;
            console.log('Matched BrandName to BrandId:', brandId);
          }
        }

        // If UOMId is not present but UOMName is, find the ID from uoms array
        let uomId = itemData.UOMId || '';
        if (!uomId && itemData.UOMName) {
          const matchedUOM = uoms.find(uom => uom.Name === itemData.UOMName);
          if (matchedUOM) {
            uomId = matchedUOM.Id;
            console.log('Matched UOMName to UOMId:', uomId);
          }
        }

        // Resolve BankId — convert to string to match native select option values
        let bankId = itemData.BankId != null && itemData.BankId !== '' ? String(itemData.BankId) : '';

        // Only repopulate the form if this is a new session OR if the lookup IDs
        // changed (i.e. a name-to-ID match resolved that wasn't available before).
        setFormData(prev => {
          const resolvedCategoryId = categoryId || prev.CategoryId;
          const resolvedBrandId = brandId || prev.BrandId;
          const resolvedUomId = uomId || prev.UOMId;
          // On a new session always overwrite; on a re-run only update resolved IDs.
          if (!isNewSession) {
            return {
              ...prev,
              CategoryId: resolvedCategoryId,
              BrandId: resolvedBrandId,
              UOMId: resolvedUomId,
            };
          }
          return {
            Id: itemData.Id || '',
            Name: itemData.Name || '',
            BarCode: itemData.BarCode || '',
            Code: itemData.Code || '',
            HSNCode: itemData.HSNCode || '',
            CategoryId: resolvedCategoryId,
            BrandId: resolvedBrandId,
            UOMId: resolvedUomId,
            GSTRateId: itemData.GSTRateId || '',
            GSTRate: itemData.GSTRate ?? 0,
            MRP: itemData.MRP ?? 0,
            PurchaseRate: itemData.PurchaseRate ?? 0,
            MOP1: itemData.MOP1 ?? 0,
            MOP2: itemData.MOP2 ?? 0,
            MOP3: itemData.MOP3 ?? 0,
            MOP4: itemData.MOP4 ?? 0,
            MOP5: itemData.MOP5 ?? 0,
            DealersPrice: itemData.DealersPrice ?? 0,
            MinRequiredQty: itemData.MinRequiredQty ?? 0,
            AlertLevelStock: itemData.AlertLevelStock ?? 0,
            IsProduct: itemData.IsProduct || false,
            IsReturnable: itemData.IsReturnable || false,
            ReturnDays: itemData.ReturnDays ?? 0,
            HasWarranty: itemData.HasWarranty || false,
            WarrantyDays: itemData.WarrantyDays ?? 0,
            IsBundle: itemData.IsBundle || false,
            IsTradingItem: itemData.IsTradingItem || false,
            IsService: itemData.IsService || false,
            IsRawMaterial: itemData.IsRawMaterial || false,
            IsInstallation: itemData.IsInstallation || false,
            BankId: bankId,
            SubCategoryId: itemData.SubCategoryId ? String(itemData.SubCategoryId) : '',
            StarRating: itemData.StarRating ?? 0,
          };
        });
        // Load existing opening balances for edit/view
        // Index.js already merges OpeningBalances onto itemData from the API response
        if (isNewSession) {
          setApiOpeningBalances(itemData.OpeningBalances || []);
        }
      } else if (isNewSession) {
        console.log('Creating new item');
        // Reset form for create mode
        setFormData({
          Name: '',
          BarCode: '',
          Code: '',
          HSNCode: '',
          CategoryId: '',
          BrandId: '',
          UOMId: '',
          GSTRateId: '',
          GSTRate: '',
          MRP: '',
          PurchaseRate: '',
          MOP1: '',
          MOP2: '',
          MOP3: '',
          MOP4: '',
          MOP5: '',
          DealersPrice: '',
          MinRequiredQty: '',
          AlertLevelStock: '',
          IsProduct: false,
          IsReturnable: false,
          ReturnDays: '',
          HasWarranty: false,
          WarrantyDays: '',
          IsBundle: false,
          IsTradingItem: true,
          IsService: false,
          IsRawMaterial: false,
          IsInstallation: false,
          BankId: '',
          SubCategoryId: '',
          StarRating: 0
        });
        setApiOpeningBalances(null);
      }
      if (isNewSession) {
        setErrors({});
        setSelectedVendor(null);
        setItemVendors([]);
        setInactiveVendors([]);
        setVendorDropdownKey(0);
        setBundleItems([]);
        setSelectedBundleItem(null);
        setBundleQuantity('');
        setBundleDropdownKey(0);
        setProductionItems([]);
        setSelectedProductionItem(null);
        setProductionQuantity('');
        setProductionRate('');
        setProductionDropdownKey(0);
        setOffersAndWarranty([]);
        setNewOfferText('');
        setItemSpecs([]);
        setNewSpecText('');
      }

      // Fetch existing vendor relations for view mode
      if (isNewSession && itemData && itemData.Id && mode !== 'create') {
        setVendorLoading(true);
        fetchData('ItemSupplierRelationalApi', `/ItemSupplierDetailsList/${itemData.Id}`, token)
          .then(res => {
            const list = res?.response_data || res || [];
            setItemVendors(Array.isArray(list) ? list : []);
          })
          .catch(() => setItemVendors([]))
          .finally(() => setVendorLoading(false));

        setInactiveVendorLoading(true);
        fetchData('ItemSupplierRelationalApi', `/ItemSupplierInActiveDetailsList/${itemData.Id}`, token)
          .then(res => {
            const list = res?.response_data || res || [];
            setInactiveVendors(Array.isArray(list) ? list : []);
          })
          .catch(() => setInactiveVendors([]))
          .finally(() => setInactiveVendorLoading(false));

        // Load bundle relations from itemData (comes with the Item API response)
        const bundleList = itemData.BundleRelations || [];
        const enrichedBundle = bundleList.map(b => {
          const matchedItem = existingItems.find(i => i.Id === b.ItemId);
          return { ...b, ItemCode: b.ItemCode || matchedItem?.Code || '' };
        });
        setBundleItems(enrichedBundle);

        // Load production relations from itemData (comes with the Item API response)
        const productionList = itemData.ProductionRelations || [];
        const enrichedProduction = productionList.map(p => {
          const matchedItem = existingItems.find(i => i.Id === p.ItemId);
          return { ...p, ItemCode: p.ItemCode || matchedItem?.Code || '' };
        });
        setProductionItems(enrichedProduction);

        // Fetch Item Offers & Warranty
        setOffersLoading(true);
        fetchData('ItemOffersAndWarrantyApi', `/ItemOffersAndWarrantyList/${itemData.Id}`, token)
          .then(res => {
            const list = res?.response_data || res || [];
            setOffersAndWarranty(Array.isArray(list) ? list : []);
          })
          .catch(() => setOffersAndWarranty([]))
          .finally(() => setOffersLoading(false));

        // Load Item Specs directly from itemData (already fetched in Index.js via ItemApi)
        const specList = itemData.ItemSpecs || [];
        setItemSpecs(specList.filter(s => s.IsActive !== false));
      }
    } else {
      // Modal closed — reset session key so re-opening the same item is treated as fresh
      initializedKeyRef.current = null;
    }
  }, [open, itemData, mode, categories, brands, uoms]);

  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        const data = response.response_data;
        setBranches(Array.isArray(data) ? data : [data]);
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
    setBranchesLoading(false);
  };

  // Merge branches with saved opening balances once both are ready
  useEffect(() => {
    if (branches.length === 0) return;
    const today = new Date().toISOString().split('T')[0];

    if (mode === 'create') {
      setFormData(prev => ({
        ...prev,
        OpeningBalances: branches.map(b => ({
          Id: 0,
          BranchId: b.Id,
          BranchName: b.Name || b.BranchName || '',
          OpeningBalance: 0,
          OpeningDate: today
        }))
      }));
    } else if ((mode === 'edit' || mode === 'view') && apiOpeningBalances !== null) {
      const merged = branches.map(b => {
        const found = apiOpeningBalances.find(r => r.BranchId === b.Id);
        return found
          ? { ...found, Id: found.Id || 0, BranchName: b.Name || b.BranchName || found.BranchName || '' }
          : { Id: 0, BranchId: b.Id, BranchName: b.Name || b.BranchName || '', OpeningBalance: 0, OpeningDate: today };
      });
      setFormData(prev => ({ ...prev, OpeningBalances: merged }));
    }
  }, [branches, mode, apiOpeningBalances]);

  const handleOpeningBalanceChange = (branchId, field, value) => {
    setFormData(prev => ({
      ...prev,
      OpeningBalances: (prev.OpeningBalances || []).map(row =>
        row.BranchId === branchId ? { ...row, [field]: value } : row
      )
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === 'checkbox') {
      // Item type toggles are mutually exclusive - only one can be active at a time
      const itemTypeFields = ['IsTradingItem', 'IsProduct', 'IsRawMaterial', 'IsBundle', 'IsService'];

      if (itemTypeFields.includes(name)) {
        if (checked) {
          // Turn on the selected one, turn off all others
          const updates = {};
          itemTypeFields.forEach(field => {
            updates[field] = field === name;
          });
          // If IsBundle is selected, also enable IsProduct (Bundle requires Product)
          // Actually Bundle/Kit is its own type now, no dependency
          // If switching away from IsProduct, also turn off IsBundle
          if (name !== 'IsProduct' && name !== 'IsBundle') {
            updates.IsBundle = false;
          }
          setFormData(prev => ({ ...prev, ...updates }));
        } else {
          // Turning off the current selection
          setFormData(prev => ({
            ...prev,
            [name]: false,
            // If IsProduct is turned off, also turn off IsBundle
            ...(name === 'IsProduct' ? { IsBundle: false } : {})
          }));
        }
        return;
      }

      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }

    // Validate HSN Code - only allow digits
    if (name === 'HSNCode' && value) {
      const hsnPattern = /^[0-9]*$/;
      if (!hsnPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }

    // Handle category selection - auto-populate HSN Code and GST Rate
    if (name === 'CategoryId' && value) {
      const selectedCategory = categories.find(cat => cat.Id === parseInt(value));
      if (selectedCategory) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          SubCategoryId: '',
          HSNCode: selectedCategory.HSNCode || prev.HSNCode,
          GSTRateId: selectedCategory.GSTRateId ? String(selectedCategory.GSTRateId) : prev.GSTRateId,
          GSTRate: selectedCategory.GSTRate != null ? selectedCategory.GSTRate : prev.GSTRate
        }));

        // Clear related errors
        setErrors(prev => ({
          ...prev,
          [name]: '',
          HSNCode: selectedCategory.HSNCode ? '' : prev.HSNCode,
          GSTRate: selectedCategory.GSTRateId ? '' : prev.GSTRate
        }));
        return;
      }
    }

    // Handle numeric inputs
    if (['GSTRate', 'MinRequiredQty', 'AlertLevelStock', 'MRP', 'DealersPrice', 'PurchaseRate', 'MOP1', 'MOP2', 'MOP3', 'MOP4', 'MOP5', 'ReturnDays', 'WarrantyDays'].includes(name)) {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name?.toString().trim()) {
      newErrors.Name = 'Item name is required';
    }

    if (!formData.CategoryId) {
      newErrors.CategoryId = 'Category is required';
    }

    if (!formData.GSTRate && formData.GSTRate !== 0) {
      newErrors.GSTRate = 'GST Rate is required';
    } else {
      const GSTRate = parseFloat(formData.GSTRate);
      if (isNaN(GSTRate) || GSTRate < 0 || GSTRate > 100) {
        newErrors.GSTRate = 'GST Rate must be between 0 and 100';
      }
    }

    if (!formData.HSNCode?.toString().trim()) {
      newErrors.HSNCode = 'HSN Code is required';
    }

    // MRP must be greater than 0
    const mrp = parseFloat(formData.MRP);
    if (!mrp || mrp <= 0) {
      newErrors.MRP = 'MRP must be greater than 0';
    }

    // Dealers Price must be greater than 0
    const dealersPrice = parseFloat(formData.DealersPrice);
    if (!dealersPrice || dealersPrice <= 0) {
      newErrors.DealersPrice = 'Dealer Price must be greater than 0';
    }

    // Purchase Rate must be greater than 0
    const purchaseRate = parseFloat(formData.PurchaseRate);
    if (!purchaseRate || purchaseRate <= 0) {
      newErrors.PurchaseRate = 'Purchase Rate must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateItem = () => {
    // Check if an item with the same name exists in the list (case-insensitive)
    const duplicate = existingItems.find(item => {
      // Skip the current item in edit mode
      if (mode === 'edit' && itemData && item.Id === itemData.Id) {
        return false;
      }

      // Check if name matches (case-insensitive)
      const nameMatch = item.Name?.trim().toLowerCase() === formData.Name?.trim().toLowerCase();

      return nameMatch;
    });

    return duplicate;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for duplicate item
    const duplicate = checkDuplicateItem();
    if (duplicate) {
      setDuplicateMessage(`An item with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const itemPayload = {
        Name: formData.Name,
        BarCode: formData.BarCode || null,
        Code: formData.Code || null,
        HSNCode: formData.HSNCode,
        CategoryId: parseInt(formData.CategoryId) || null,
        BrandId: parseInt(formData.BrandId) || null,
        UOMId: parseInt(formData.UOMId) || null,
        GSTRateId: parseInt(formData.GSTRateId) || 0,
        GSTRate: parseFloat(formData.GSTRate) || 0,
        MRP: parseFloat(formData.MRP) || 0,
        DealersPrice: parseFloat(formData.DealersPrice) || 0,
        PurchaseRate: parseFloat(formData.PurchaseRate) || 0,
        MOP1: parseFloat(formData.MOP1) || 0,
        MOP2: parseFloat(formData.MOP2) || 0,
        MOP3: parseFloat(formData.MOP3) || 0,
        MOP4: parseFloat(formData.MOP4) || 0,
        MOP5: parseFloat(formData.MOP5) || 0,
        MinRequiredQty: parseFloat(formData.MinRequiredQty) || 0,
        AlertLevelStock: parseFloat(formData.AlertLevelStock) || 0,
        IsProduct: formData.IsProduct || false,
        IsReturnable: formData.IsReturnable || false,
        ReturnDays: parseInt(formData.ReturnDays) || 0,
        HasWarranty: formData.HasWarranty || false,
        WarrantyDays: parseInt(formData.WarrantyDays) || 0,
        IsBundle: formData.IsBundle || false,
        IsTradingItem: formData.IsTradingItem || false,
        IsService: formData.IsService || false,
        IsRawMaterial: formData.IsRawMaterial || false,
        IsInstallation: formData.IsInstallation || false,
        BankId: formData.BankId !== '' && formData.BankId !== null ? parseInt(formData.BankId) : null,
        SubCategoryId: formData.SubCategoryId !== '' && formData.SubCategoryId !== null ? parseInt(formData.SubCategoryId) : null,
        StarRating: formData.StarRating ? parseInt(formData.StarRating) : null,
        CompanyId: companyId,
        IsActive: itemData?.IsActive ?? true
      };

      const openingBalances = (formData.OpeningBalances || []).map(row => ({
        Id: row.Id || 0,
        BranchId: row.BranchId,
        ItemId: mode === 'edit' ? (itemData?.Id || 0) : 0,
        CompanyId: companyId,
        OpeningBalance: parseFloat(row.OpeningBalance) || 0,
        OpeningDate: row.OpeningDate ? row.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
        IsActive: true
      }));

      if (mode === 'edit') {
        if (!itemData || !itemData.Id) {
          console.error('Cannot update: Missing item ID');
          alert('Error: Cannot update item - missing ID');
          return;
        }
        itemPayload.Id = itemData.Id;
        itemPayload.ModifiedBy = userId;
        itemPayload.ModifiedDate = new Date();
        if (itemData.CreatedBy) itemPayload.CreatedBy = itemData.CreatedBy;
        if (itemData.CreatedDate) itemPayload.CreatedDate = itemData.CreatedDate;

        // Track GST rate change
        if (itemData.GSTRate != null && parseFloat(itemData.GSTRate) !== (parseFloat(formData.GSTRate) || 0)) {
          itemPayload.OldGSTRate = parseFloat(itemData.GSTRate);
          itemPayload.GSTRateLastUpdatedDate = new Date();
          itemPayload.GSTRateLastUpdatedUserName = username || '';
        }

        const bundleRelations = formData.IsBundle ? bundleItems.map(b => ({
          Id: b.Id || 0,
          ProductId: itemData.Id,
          ItemId: b.ItemId || b._itemId,
          Quantity: b.Quantity,
          ProductName: formData.Name,
          ItemName: b.ItemName || '',
          IsActive: true,
          ModifiedBy: userId,
          ModifiedDate: new Date()
        })) : [];

        const productionRelations = formData.IsProduct ? productionItems.map(p => ({
          Id: p.Id || 0,
          ProductId: itemData.Id,
          ItemId: p.ItemId || p._itemId,
          Quantity: p.Quantity,
          Rate: p.Rate,
          ProductName: formData.Name,
          ItemName: p.ItemName || '',
          IsActive: true,
          ModifiedBy: userId,
          ModifiedDate: new Date()
        })) : [];

        const dataToSend = { Item: itemPayload, OpeningBalances: openingBalances, ItemBundleRelation: bundleRelations, ItemProductionRelation: productionRelations, ItemSpec: itemSpecs.map(s => ({
          Id: s.Id || 0,
          ItemId: itemData.Id,
          CompanyId: companyId,
          Spec: s.Spec,
          IsActive: true,
          CreatedBy: s.Id ? (s.CreatedBy || userId) : userId,
          CreatedDate: s.Id ? (s.CreatedDate || new Date()) : new Date(),
          ModifiedBy: s.Id ? userId : null,
          ModifiedDate: s.Id ? new Date() : null
        })) };
        const response = await updateData('ItemApi', itemData.Id, dataToSend, token);
      logAudit(AUDIT_ACTION.UPDATE, 'ItemApi', itemData.Id, { ...itemData }, { ...formData });
        console.log('Update response:', response);
      } else {
        itemPayload.CreatedBy = userId;
        itemPayload.CreatedDate = new Date();

        const bundleRelations = formData.IsBundle ? bundleItems.map(b => ({
          Id: 0,
          ProductId: 0,
          ItemId: b.ItemId || b._itemId,
          Quantity: b.Quantity,
          ProductName: formData.Name,
          ItemName: b.ItemName || '',
          IsActive: true,
          CreatedBy: userId,
          CreatedDate: new Date()
        })) : [];

        const productionRelations = formData.IsProduct ? productionItems.map(p => ({
          Id: 0,
          ProductId: 0,
          ItemId: p.ItemId || p._itemId,
          Quantity: p.Quantity,
          Rate: p.Rate,
          ProductName: formData.Name,
          ItemName: p.ItemName || '',
          IsActive: true,
          CreatedBy: userId,
          CreatedDate: new Date()
        })) : [];

        const dataToSend = { Item: itemPayload, OpeningBalances: openingBalances, ItemBundleRelation: bundleRelations, ItemProductionRelation: productionRelations, ItemSpec: itemSpecs.map(s => ({
          Id: 0,
          ItemId: 0,
          CompanyId: companyId,
          Spec: s.Spec,
          IsActive: true,
          CreatedBy: userId,
          CreatedDate: new Date()
        })) };
        const response = await postData('ItemApi', '', dataToSend, token);
      logAudit(AUDIT_ACTION.CREATE, 'ItemApi', response?.Id || 0, null, { Name: formData.Name || '', Category: formData.CategoryName || '' });
        console.log('Create response:', response);

        // Save vendor relations for newly created item
        if (itemVendors.length > 0) {
          const newItemId = response?.response_data?.Item?.Id || response?.Item?.Id || response?.response_data?.Id || response?.Id;
          if (newItemId) {
            await Promise.allSettled(itemVendors.map(v =>
              postData('ItemSupplierRelationalApi', '', {
                CompanyId: companyId,
                ItemId: newItemId,
                MasterId: v.MasterId || v.Id,
                ItemName: formData.Name,
                MasterName: v.Name || '',
                IsActive: true,
                CreatedBy: userId,
                CreatedDate: new Date()
              }, token)
            ));
          }
        }

      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = () => {
    if (!selectedVendor) return;
    const vendorKey = selectedVendor.MasterId || selectedVendor.Id;
    const alreadyAdded = itemVendors.some(v => (v.MasterId || v._masterId) === vendorKey);
    if (alreadyAdded) return;
    // Tag as new (not yet saved) using _isNew flag
    setItemVendors(prev => [...prev, { ...selectedVendor, _isNew: true, _masterId: vendorKey }]);
    setSelectedVendor(null);
    setVendorDropdownKey(prev => prev + 1);
  };

  const handleSubmitVendors = async () => {
    if (!itemData || !itemData.Id || itemVendors.length === 0) return;
    try {
      setVendorLoading(true);
      // Only send newly added vendors (tagged with _isNew)
      const newVendors = itemVendors.filter(v => v._isNew);
      if (newVendors.length === 0) return;

      const payload = newVendors.map(v => ({
        CompanyId: companyId,
        ItemId: itemData.Id,
        MasterId: v._masterId,
        ItemName: itemData.Name || '',
        MasterName: v.Name || '',
        IsActive: true,
        CreatedBy: userId,
        CreatedDate: new Date()
      }));

      await postData('ItemSupplierRelationalApi', '', payload, token);
      await refreshVendorLists();
    } catch (err) {
      console.error('Error submitting vendors:', err);
    } finally {
      setVendorLoading(false);
    }
  };

  const handleConfirmActivateVendor = async () => {
    if (!vendorToActivate) return;
    try {
      setInactiveVendorLoading(true);
      await activateData('ItemSupplierRelationalApi', vendorToActivate.Id, token);
      await refreshVendorLists();
    } catch (err) {
      console.error('Error activating vendor:', err);
    } finally {
      setInactiveVendorLoading(false);
      setVendorActivateConfirm(false);
      setVendorToActivate(null);
    }
  };

  const handleRemoveVendor = async (vendor) => {
    // If saved vendor (has Id), call delete API then refresh
    if (vendor.Id) {
      setVendorToDelete(vendor);
      setVendorDeleteConfirm(true);
    } else {
      // Locally added, just remove from state
      setItemVendors(prev => prev.filter(v => (v.MasterId || v._masterId) !== (vendor.MasterId || vendor._masterId)));
    }
  };

  const refreshVendorLists = async () => {
    if (!itemData?.Id) return;
    setVendorLoading(true);
    setInactiveVendorLoading(true);
    try {
      const [activeRes, inactiveRes] = await Promise.allSettled([
        fetchData('ItemSupplierRelationalApi', `/ItemSupplierDetailsList/${itemData.Id}`, token),
        fetchData('ItemSupplierRelationalApi', `/ItemSupplierInActiveDetailsList/${itemData.Id}`, token)
      ]);
      const activeList = activeRes.status === 'fulfilled' ? (activeRes.value?.response_data || activeRes.value || []) : [];
      const inactiveList = inactiveRes.status === 'fulfilled' ? (inactiveRes.value?.response_data || inactiveRes.value || []) : [];
      setItemVendors(Array.isArray(activeList) ? activeList : []);
      setInactiveVendors(Array.isArray(inactiveList) ? inactiveList : []);
    } catch {
      // keep existing data on error
    } finally {
      setVendorLoading(false);
      setInactiveVendorLoading(false);
    }
  };

  const handleConfirmDeleteVendor = async () => {
    if (!vendorToDelete) return;
    try {
      setVendorLoading(true);
      await deleteData('ItemSupplierRelationalApi', vendorToDelete.Id, token);
      await refreshVendorLists();
    } catch (err) {
      console.error('Error deleting vendor:', err);
    } finally {
      setVendorLoading(false);
      setVendorDeleteConfirm(false);
      setVendorToDelete(null);
    }
  };

  // Bundle Item handlers
  const handleAddBundleItem = () => {
    if (!selectedBundleItem || !bundleQuantity || parseFloat(bundleQuantity) <= 0) return;
    const itemId = selectedBundleItem.Id;
    const alreadyAdded = bundleItems.some(b => (b.ItemId || b._itemId) === itemId);
    if (alreadyAdded) return;
    setBundleItems(prev => [...prev, {
      _isNew: true,
      _itemId: itemId,
      ItemId: itemId,
      ItemName: selectedBundleItem.Name || '',
      ItemCode: selectedBundleItem.Code || '',
      Quantity: parseFloat(bundleQuantity)
    }]);
    setSelectedBundleItem(null);
    setBundleQuantity('');
    setBundleDropdownKey(prev => prev + 1);
  };

  const handleRemoveBundleItem = (bundleItem) => {
    if (bundleItem.Id) {
      setBundleToDelete(bundleItem);
      setBundleDeleteConfirm(true);
    } else {
      setBundleItems(prev => prev.filter(b => (b.ItemId || b._itemId) !== (bundleItem.ItemId || bundleItem._itemId)));
    }
  };

  const handleConfirmDeleteBundleItem = async () => {
    if (!bundleToDelete) return;
    try {
      setBundleLoading(true);
      await deleteData('ItemBundleRelationApi', bundleToDelete.Id, token);
      // Refresh bundle list
      if (itemData?.Id) {
        const res = await fetchData('ItemBundleRelationApi', `/ItemBundleDetailsList/${itemData.Id}`, token);
        const list = res?.response_data || res || [];
        setBundleItems(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error deleting bundle item:', err);
    } finally {
      setBundleLoading(false);
      setBundleDeleteConfirm(false);
      setBundleToDelete(null);
    }
  };

  const handleBundleQuantityChange = (bundleItem, newQty) => {
    setBundleItems(prev => prev.map(b =>
      (b.ItemId || b._itemId) === (bundleItem.ItemId || bundleItem._itemId)
        ? { ...b, Quantity: parseFloat(newQty) || 0 }
        : b
    ));
  };

  // Production Item handlers
  const handleAddProductionItem = () => {
    if (!selectedProductionItem || !productionQuantity || parseFloat(productionQuantity) <= 0) return;
    const itemId = selectedProductionItem.Id;
    const alreadyAdded = productionItems.some(p => (p.ItemId || p._itemId) === itemId);
    if (alreadyAdded) return;
    setProductionItems(prev => [...prev, {
      _isNew: true,
      _itemId: itemId,
      ItemId: itemId,
      ItemName: selectedProductionItem.Name || '',
      ItemCode: selectedProductionItem.Code || '',
      Quantity: parseFloat(productionQuantity),
      Rate: parseFloat(productionRate) || 0
    }]);
    setSelectedProductionItem(null);
    setProductionQuantity('');
    setProductionRate('');
    setProductionDropdownKey(prev => prev + 1);
  };

  const handleRemoveProductionItem = (prodItem) => {
    if (prodItem.Id) {
      setProductionToDelete(prodItem);
      setProductionDeleteConfirm(true);
    } else {
      setProductionItems(prev => prev.filter(p => (p.ItemId || p._itemId) !== (prodItem.ItemId || prodItem._itemId)));
    }
  };

  const handleConfirmDeleteProductionItem = async () => {
    if (!productionToDelete) return;
    try {
      setProductionLoading(true);
      await deleteData('ItemProductionRelationApi', productionToDelete.Id, token);
      // Refresh production list
      if (itemData?.Id) {
        const res = await fetchData('ItemProductionRelationApi', `/ItemProductionDetailsList/${itemData.Id}`, token);
        const list = res?.response_data || res || [];
        setProductionItems(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error deleting production item:', err);
    } finally {
      setProductionLoading(false);
      setProductionDeleteConfirm(false);
      setProductionToDelete(null);
    }
  };


  const handleProductionFieldChange = (prodItem, field, value) => {
    setProductionItems(prev => prev.map(p =>
      (p.ItemId || p._itemId) === (prodItem.ItemId || prodItem._itemId)
        ? { ...p, [field]: parseFloat(value) || 0 }
        : p
    ));
  };

  // Item Offers & Warranty handlers
  const handleAddOffer = async () => {
    if (!newOfferText.trim()) return;
    try {
      setOffersLoading(true);
      const payload = {
        ItemId: itemData?.Id || 0,
        OffersAndWarranty: newOfferText.trim(),
        IsActive: true,
        CreatedBy: userId,
        CreatedDate: new Date()
      };
      if (itemData?.Id) {
        // Saved item — persist immediately
        await postData('ItemOffersAndWarrantyApi', '', payload, token);
        const res = await fetchData('ItemOffersAndWarrantyApi', `/ItemOffersAndWarrantyList/${itemData.Id}`, token);
        const list = res?.response_data || res || [];
        setOffersAndWarranty(Array.isArray(list) ? list : []);
      } else {
        // New item — keep locally, will be saved after item is created
        setOffersAndWarranty(prev => [...prev, { ...payload, _isNew: true, _tempId: Date.now() }]);
      }
      setNewOfferText('');
    } catch (err) {
      console.error('Error adding offer:', err);
    } finally {
      setOffersLoading(false);
    }
  };

  const handleRemoveOffer = (offer) => {
    if (offer.Id) {
      setOfferToDelete(offer);
      setOfferDeleteConfirm(true);
    } else {
      setOffersAndWarranty(prev => prev.filter(o => o._tempId !== offer._tempId));
    }
  };

  const handleConfirmDeleteOffer = async () => {
    if (!offerToDelete) return;
    try {
      setOffersLoading(true);
      await deleteData('ItemOffersAndWarrantyApi', offerToDelete.Id, token);
      if (itemData?.Id) {
        const res = await fetchData('ItemOffersAndWarrantyApi', `/ItemOffersAndWarrantyList/${itemData.Id}`, token);
        const list = res?.response_data || res || [];
        setOffersAndWarranty(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.error('Error deleting offer:', err);
    } finally {
      setOffersLoading(false);
      setOfferDeleteConfirm(false);
      setOfferToDelete(null);
    }
  };

  // Item Spec handlers
  const handleAddSpec = async () => {
    if (!newSpecText.trim()) return;
    // Always add locally — will be saved when user clicks Save
    setItemSpecs(prev => [...prev, {
      Id: 0,
      ItemId: itemData?.Id || 0,
      CompanyId: companyId,
      Spec: newSpecText.trim(),
      IsActive: true,
      CreatedBy: userId,
      CreatedDate: new Date(),
      _isNew: true,
      _tempId: Date.now()
    }]);
    setNewSpecText('');
  };

  const handleRemoveSpec = (spec) => {
    if (spec._isNew || !spec.Id) {
      // Locally added — just remove from state
      setItemSpecs(prev => prev.filter(s => s._tempId !== spec._tempId));
    } else {
      // Saved spec — show confirm dialog
      setSpecToDelete(spec);
      setSpecDeleteConfirm(true);
    }
  };

  const handleConfirmDeleteSpec = async () => {
    if (!specToDelete) return;
    // Optimistic UI — remove only the target spec from local state
    setItemSpecs(prev => prev.filter(s => {
      if (specToDelete.Id) return s.Id !== specToDelete.Id;
      return s._tempId !== specToDelete._tempId;
    }));
    setSpecDeleteConfirm(false);
    setSpecToDelete(null);
    // Fire API in background if it's a saved record
    if (specToDelete.Id) {
      try {
        await deleteData('ExecutivedashboardApi', specToDelete.Id, token);
      } catch (err) {
        console.error('Error deleting spec from server:', err);
        // Restore the spec if API fails
        setItemSpecs(prev => [...prev, specToDelete]);
      }
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Item';
      case 'edit': return 'Edit Item';
      case 'view': return 'Item Details';
      default: return 'Item';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '10px',
          display: 'flex',
          flexDirection: 'column',
          height: isMobile ? '100vh' : '90vh',
          overflow: 'hidden'
        }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
            color: 'white',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <InventoryIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              style={{ color: 'white', padding: '2px' }}
              size="small"
            >
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{
            padding: '12px 16px',
            paddingBottom: '8px',
            flex: 1,
            overflowY: 'auto'
          }}>
            <Grid container spacing={1.5}>
              {/* Basic Information Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#764ba2',
                  fontSize: '13px'
                }}>
                  Basic Information
                </Typography>
              </Grid>

              {/* Item Name */}
              <Grid item xs={12} sm={8}>
                <TextField
                  name="Name"
                  label="Item Name *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter item name"
                  inputProps={{ maxLength: 150 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Bar Code and Code */}
              <Grid item xs={12} sm={2}>
                <TextField
                  name="BarCode"
                  label="Bar Code"
                  value={formData.BarCode}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter bar code"
                  inputProps={{ maxLength: 50 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="Code"
                  label="Code"
                  value={formData.Code}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter code"
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>

              {/* HSN Code */}
              <Grid item xs={12} sm={2}>
                <TextField
                  name="HSNCode"
                  label="HSN Code *"
                  value={formData.HSNCode}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.HSNCode}
                  helperText={errors.HSNCode}
                  disabled={isReadOnly}
                  placeholder="Enter HSN code"
                  inputProps={{ maxLength: 10 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Category, SubCategory, StarRating, Brand — all on one row */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  options={categories}
                  getOptionLabel={(option) => option.Name || ''}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  value={categories.find(c => String(c.Id) === String(formData.CategoryId)) || null}
                  onChange={(_, newVal) => {
                    if (newVal) {
                      setFormData(prev => ({
                        ...prev,
                        CategoryId: String(newVal.Id),
                        SubCategoryId: '',
                        HSNCode: newVal.HSNCode || prev.HSNCode,
                        GSTRateId: newVal.GSTRateId ? String(newVal.GSTRateId) : prev.GSTRateId,
                        GSTRate: newVal.GSTRate != null ? newVal.GSTRate : prev.GSTRate
                      }));
                      setErrors(prev => ({
                        ...prev,
                        CategoryId: '',
                        HSNCode: newVal.HSNCode ? '' : prev.HSNCode,
                        GSTRate: newVal.GSTRateId ? '' : prev.GSTRate
                      }));
                    } else {
                      setFormData(prev => ({ ...prev, CategoryId: '', SubCategoryId: '' }));
                    }
                  }}
                  disabled={isReadOnly}
                  renderOption={(props, option) => (
                    <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Name}</li>
                  )}
                  ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category *"
                      error={!!errors.CategoryId}
                      helperText={errors.CategoryId}
                      InputProps={{ ...params.InputProps, style: inputStyle }}
                      InputLabelProps={{ style: labelStyle }}
                      FormHelperTextProps={{ style: helperStyle }}
                    />
                  )}
                />
              </Grid>

              {/* SubCategory — filtered by selected CategoryId */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  options={formData.CategoryId
                    ? subCategories.filter(s => String(s.CategoryId) === String(formData.CategoryId))
                    : []}
                  getOptionLabel={(option) => option.Name || ''}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  value={subCategories.find(s => String(s.Id) === String(formData.SubCategoryId)) || null}
                  onChange={(_, newVal) => {
                    setFormData(prev => ({ ...prev, SubCategoryId: newVal ? String(newVal.Id) : '' }));
                  }}
                  disabled={isReadOnly || !formData.CategoryId || subCategoriesLoading}
                  loading={subCategoriesLoading}
                  noOptionsText={formData.CategoryId ? 'No sub-categories for this category' : 'Select a category first'}
                  renderOption={(props, option) => (
                    <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Name}</li>
                  )}
                  ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sub Category"
                      InputProps={{ ...params.InputProps, style: inputStyle }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}
                />
              </Grid>

              {/* Star Rating */}
              <Grid item xs={12} sm={2}>
                <Box>
                  <Typography style={{ ...labelStyle, color: 'rgba(0,0,0,0.6)', marginBottom: '4px' }}>
                    Star Rating
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <Box
                        key={star}
                        onClick={() => {
                          if (!isReadOnly) {
                            setFormData(prev => ({ ...prev, StarRating: prev.StarRating === star ? 0 : star }));
                          }
                        }}
                        style={{ cursor: isReadOnly ? 'default' : 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        {star <= (formData.StarRating || 0)
                          ? <StarIcon style={{ color: '#f5a623', fontSize: '20px' }} />
                          : <StarBorderIcon style={{ color: '#bbb', fontSize: '20px' }} />
                        }
                      </Box>
                    ))}
                    {formData.StarRating > 0 && (
                      <Typography style={{ fontSize: '11px', color: '#666', marginLeft: '2px' }}>
                        {formData.StarRating}/5
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Brand */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  options={brands}
                  getOptionLabel={(option) => option.Name || ''}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  value={brands.find(b => String(b.Id) === String(formData.BrandId)) || null}
                  onChange={(_, newVal) => {
                    setFormData(prev => ({ ...prev, BrandId: newVal ? String(newVal.Id) : '' }));
                  }}
                  disabled={isReadOnly}
                  renderOption={(props, option) => (
                    <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Name}</li>
                  )}
                  ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Brand"
                      InputProps={{ ...params.InputProps, style: inputStyle }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  size="small"
                  options={uoms}
                  getOptionLabel={(option) => option.Name || ''}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  value={uoms.find(u => String(u.Id) === String(formData.UOMId)) || null}
                  onChange={(_, newVal) => {
                    setFormData(prev => ({ ...prev, UOMId: newVal ? String(newVal.Id) : '' }));
                  }}
                  disabled={isReadOnly}
                  renderOption={(props, option) => (
                    <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Name}</li>
                  )}
                  ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="UOM"
                      InputProps={{ ...params.InputProps, style: inputStyle }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  size="small"
                  options={banks}
                  getOptionLabel={(option) => option.Name || ''}
                  isOptionEqualToValue={(option, value) => (option.Id || option.MasterId) === (value.Id || value.MasterId)}
                  value={banks.find(b => String(b.Id || b.MasterId) === String(formData.BankId)) || null}
                  onChange={(_, newVal) => {
                    setFormData(prev => ({ ...prev, BankId: newVal ? String(newVal.Id || newVal.MasterId) : '' }));
                  }}
                  disabled={isReadOnly}
                  renderOption={(props, option) => (
                    <li {...props} key={option.Id || option.MasterId} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Name}</li>
                  )}
                  ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bank"
                      InputProps={{ ...params.InputProps, style: inputStyle }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}
                />
              </Grid>

              {/* Pricing & Tax Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '6px',
                  marginTop: '4px',
                  color: '#764ba2',
                  fontSize: '13px'
                }}>
                  Pricing & Tax
                </Typography>
              </Grid>

              {/* MRP, Purchase Rate, Sale Rate - All in one row */}
              <Grid item xs={12} sm={2}>
                <TextField
                  name="MRP"
                  label="MRP (i.-)"
                  value={formData.MRP}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  error={!!errors.MRP}
                  helperText={errors.MRP}
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="DealersPrice"
                  label="Dealers Price"
                  value={formData.DealersPrice}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  error={!!errors.DealersPrice}
                  helperText={errors.DealersPrice}
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="MOP1"
                  label="MOP1 (Market Operating Price)"
                  value={formData.MOP1}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="MOP2"
                  label="MOP2 (Market Operating Price)"
                  value={formData.MOP2}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="MOP3"
                  label="MOP3 (Market Operating Price)"
                  value={formData.MOP3}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="MOP4"
                  label="MOP4 (Market Operating Price)"
                  value={formData.MOP4}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="MOP5"
                  label="MOP5 (Market Operating Price)"
                  value={formData.MOP5}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="PurchaseRate"
                  label="Purchase Rate"
                  value={formData.PurchaseRate}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  error={!!errors.PurchaseRate}
                  helperText={errors.PurchaseRate}
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              {/* GST Rate, Min Required Qty - All in one row */}
              <Grid item xs={12} sm={2}>
                <Autocomplete
                  size="small"
                  options={gstRates}
                  getOptionLabel={(option) => `${option.Rate}%`}
                  isOptionEqualToValue={(option, value) => option.Id === value.Id}
                  value={gstRates.find(r => String(r.Id) === String(formData.GSTRateId)) || null}
                  onChange={(_, newVal) => {
                    setFormData(prev => ({
                      ...prev,
                      GSTRateId: newVal ? String(newVal.Id) : '',
                      GSTRate: newVal ? newVal.Rate : ''
                    }));
                    if (errors.GSTRate) {
                      setErrors(prev => ({ ...prev, GSTRate: '' }));
                    }
                  }}
                  disabled={isReadOnly || gstRatesLoading}
                  renderOption={(props, option) => (
                    <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Rate}%</li>
                  )}
                  ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                  loading={gstRatesLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="GST Rate (%) *"
                      error={!!errors.GSTRate}
                      helperText={errors.GSTRate}
                      InputProps={{
                        ...params.InputProps,
                        style: { ...inputStyle, textAlign: 'right' },
                        endAdornment: (
                          <>
                            {gstRatesLoading ? <CircularProgress size={14} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                      InputLabelProps={{ style: labelStyle, shrink: true }}
                      FormHelperTextProps={{ style: helperStyle }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="MinRequiredQty"
                  label="Min Required Qty"
                  value={formData.MinRequiredQty}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0"
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              {/* Alert Level Stock - Full width */}
              <Grid item xs={12} sm={2}>
                <TextField
                  name="AlertLevelStock"
                  label="Alert Level Stock"
                  value={formData.AlertLevelStock}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0"
                  InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              {/* Item Specification Section */}
              <Grid item xs={12}>
                <Box style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography variant="subtitle2" style={{
                    fontWeight: 600,
                    fontSize: '12px',
                    marginBottom: '10px',
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Item Specification
                  </Typography>
                  <Box style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { name: 'IsTradingItem', label: 'Trading Item', checked: formData.IsTradingItem },
                      { name: 'IsProduct', label: 'Manufactured Product', checked: formData.IsProduct },
                      { name: 'IsRawMaterial', label: 'Raw Material', checked: formData.IsRawMaterial },
                      { name: 'IsBundle', label: 'Bundle/Kit', checked: formData.IsBundle },
                      { name: 'IsService', label: 'Service', checked: formData.IsService },
                      { name: 'IsInstallation', label: '🔧 Installation Required', checked: formData.IsInstallation, color: '#7c3aed', activeColor: '#ede9fe', activeBorder: '#7c3aed' },
                    ].map((item) => (
                      <Box
                        key={item.name}
                        onClick={() => !isReadOnly && handleChange({ target: { name: item.name, type: 'checkbox', checked: !item.checked } })}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          cursor: isReadOnly ? 'default' : 'pointer',
                          border: item.checked
                            ? `1.5px solid ${item.activeBorder || '#1976d2'}`
                            : '1.5px solid #cbd5e1',
                          backgroundColor: item.checked
                            ? (item.activeColor || '#e3f2fd')
                            : '#ffffff',
                          transition: 'all 0.2s ease',
                          userSelect: 'none'
                        }}
                      >
                        <Box style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: item.checked ? (item.color || '#1976d2') : '#94a3b8',
                          transition: 'background-color 0.2s ease'
                        }} />
                        <span style={{
                          fontSize: '12px',
                          fontWeight: item.checked ? 600 : 400,
                          color: item.checked ? (item.color || '#1565c0') : '#64748b'
                        }}>
                          {item.label}
                        </span>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* Toggles Section */}
              <Grid item xs={12}>
                <Box style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.IsReturnable}
                        onChange={handleChange}
                        name="IsReturnable"
                        disabled={isReadOnly}
                        size="small"
                      />
                    }
                    label={<span style={{ fontSize: '12px' }}>Returnable</span>}
                  />

                  {formData.IsReturnable && (
                    <TextField
                      name="ReturnDays"
                      label="Return Days"
                      value={formData.ReturnDays}
                      onChange={handleChange}
                      size="small"
                      disabled={isReadOnly}
                      placeholder="0"
                      style={{ width: '120px' }}
                      InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.HasWarranty}
                        onChange={handleChange}
                        name="HasWarranty"
                        disabled={isReadOnly}
                        size="small"
                      />
                    }
                    label={<span style={{ fontSize: '12px' }}>Has Warranty</span>}
                  />

                  {formData.HasWarranty && (
                    <TextField
                      name="WarrantyDays"
                      label="Warranty Days"
                      value={formData.WarrantyDays}
                      onChange={handleChange}
                      size="small"
                      disabled={isReadOnly}
                      placeholder="0"
                      style={{ width: '120px' }}
                      InputProps={{ style: { ...inputStyle, textAlign: 'right' } }}
                      InputLabelProps={{ style: labelStyle }}
                    />
                  )}

                  {formData.HasWarranty && (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', backgroundColor: '#fff8e1', borderRadius: '4px', border: '1px solid #ffe082' }}>
                      <InfoOutlinedIcon style={{ fontSize: '14px', color: '#f9a825' }} />
                      <span style={{ fontSize: '11px', color: '#5d4037' }}>
                        Items with warranty will be selected batch-wise during sale.
                      </span>
                    </Box>
                  )}

                </Box>
              </Grid>

              {/* Bundle Items Section - shown when IsBundle is true */}
              {formData.IsBundle && (
                <>
                  <Grid item xs={12}>
                    <Box
                      onClick={() => setBundleExpanded(prev => !prev)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        marginTop: '4px'
                      }}
                    >
                      <Typography variant="subtitle1" style={{
                        fontWeight: '600',
                        color: '#764ba2',
                        fontSize: '13px'
                      }}>
                        Bundle Items
                      </Typography>
                      <IconButton size="small" style={{ padding: '2px' }}>
                        {bundleExpanded ? (
                          <ExpandLessIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                        ) : (
                          <ExpandMoreIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                        )}
                      </IconButton>
                    </Box>
                  </Grid>

                  {bundleExpanded && (
                    <>
                      {/* Add bundle item row */}
                      {!isReadOnly && (
                        <Grid item xs={12}>
                          <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Autocomplete
                              key={bundleDropdownKey}
                              size="small"
                              options={existingItems.filter(item =>
                                !item.IsProduct &&
                                item.Id !== (itemData?.Id || 0) &&
                                !bundleItems.some(b => (b.ItemId || b._itemId) === item.Id)
                              )}
                              getOptionLabel={(option) => option.Code ? `${option.Code} - ${option.Name}` : option.Name || ''}
                              isOptionEqualToValue={(option, value) => option.Id === value.Id}
                              value={selectedBundleItem}
                              onChange={(_, newVal) => setSelectedBundleItem(newVal)}
                              style={{ flex: 1 }}
                              renderOption={(props, option) => (
                                <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Code ? `${option.Code} - ${option.Name}` : option.Name}</li>
                              )}
                              ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Item"
                                  size="small"
                                  InputProps={{ ...params.InputProps, style: inputStyle }}
                                  InputLabelProps={{ style: labelStyle }}
                                />
                              )}
                            />
                            <TextField
                              size="small"
                              type="number"
                              label="Qty"
                              value={bundleQuantity}
                              onChange={(e) => setBundleQuantity(e.target.value)}
                              style={{ width: '100px' }}
                              inputProps={{ min: 0.01, step: 0.01, style: { padding: '6px 8px', fontSize: '12px' } }}
                              InputLabelProps={{ style: labelStyle }}
                            />
                            <Tooltip title="Add Item to Bundle">
                              <span>
                                <IconButton
                                  onClick={handleAddBundleItem}
                                  disabled={!selectedBundleItem || !bundleQuantity || parseFloat(bundleQuantity) <= 0 || bundleLoading}
                                  size="small"
                                  style={{
                                    background: selectedBundleItem && bundleQuantity && parseFloat(bundleQuantity) > 0 && !bundleLoading
                                      ? 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)'
                                      : '#e0e0e0',
                                    color: 'white',
                                    borderRadius: '6px',
                                    padding: '6px'
                                  }}
                                >
                                  <AddCircleOutlineIcon style={{ fontSize: '16px' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Grid>
                      )}

                      {/* Bundle items table */}
                      <Grid item xs={12}>
                        {bundleLoading ? (
                          <Box style={{ textAlign: 'center', padding: '16px', color: '#888', fontSize: '12px', border: '1px solid #e9ecef', borderRadius: '6px' }}>Loading...</Box>
                        ) : (
                          <Box style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                            <Table size="small" style={{ tableLayout: 'fixed' }}>
                              <TableHead>
                                <TableRow style={{ background: '#f1f5f9' }}>
                                  <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '10%' }}>#</TableCell>
                                  <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '50%' }}>Item Name</TableCell>
                                  <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '25%' }}>Quantity</TableCell>
                                  {!isReadOnly && (
                                    <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '15%', textAlign: 'center' }}>Action</TableCell>
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {bundleItems.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={isReadOnly ? 3 : 4} style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '10px' }}>
                                      No bundle items added
                                    </TableCell>
                                  </TableRow>
                                ) : bundleItems.map((bundle, index) => (
                                  <TableRow key={bundle.Id || bundle._itemId || bundle.ItemId} style={{ background: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                                    <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#555' }}>{index + 1}</TableCell>
                                    <TableCell style={{ fontSize: '11px', padding: '4px 8px', fontWeight: '500' }}>{bundle.ItemCode ? `${bundle.ItemCode} - ${bundle.ItemName}` : bundle.ItemName}</TableCell>
                                    <TableCell style={{ padding: '4px 8px' }}>
                                      {isReadOnly ? (
                                        <span style={{ fontSize: '11px' }}>{bundle.Quantity}</span>
                                      ) : (
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={bundle.Quantity}
                                          onChange={(e) => handleBundleQuantityChange(bundle, e.target.value)}
                                          inputProps={{ min: 0.01, step: 0.01, style: { padding: '3px 6px', fontSize: '11px' } }}
                                          style={{ width: '80px' }}
                                          sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' } }}
                                        />
                                      )}
                                    </TableCell>
                                    {!isReadOnly && (
                                      <TableCell style={{ padding: '4px 8px', textAlign: 'center' }}>
                                        <Tooltip title="Remove">
                                          <IconButton size="small" onClick={() => handleRemoveBundleItem(bundle)} style={{ color: '#e53935', padding: '2px' }}>
                                            <DeleteOutlineIcon style={{ fontSize: '14px' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Production Items Section - shown when IsProduct is true */}
              {formData.IsProduct && (
                <>
                  <Grid item xs={12}>
                    <Box
                      onClick={() => setProductionExpanded(prev => !prev)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        marginTop: '4px'
                      }}
                    >
                      <Typography variant="subtitle1" style={{
                        fontWeight: '600',
                        color: '#764ba2',
                        fontSize: '13px'
                      }}>
                        Production Items (Raw Materials)
                      </Typography>
                      <IconButton size="small" style={{ padding: '2px' }}>
                        {productionExpanded ? (
                          <ExpandLessIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                        ) : (
                          <ExpandMoreIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                        )}
                      </IconButton>
                    </Box>
                  </Grid>

                  {productionExpanded && (
                    <>
                      {/* Add production item row */}
                      {!isReadOnly && (
                        <Grid item xs={12}>
                          <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Autocomplete
                              key={productionDropdownKey}
                              size="small"
                              options={existingItems.filter(item =>
                                item.IsRawMaterial === true &&
                                item.Id !== (itemData?.Id || 0) &&
                                !productionItems.some(p => (p.ItemId || p._itemId) === item.Id)
                              )}
                              getOptionLabel={(option) => option.Code ? `${option.Code} - ${option.Name}` : option.Name || ''}
                              isOptionEqualToValue={(option, value) => option.Id === value.Id}
                              value={selectedProductionItem}
                              onChange={(_, newVal) => setSelectedProductionItem(newVal)}
                              style={{ flex: 1 }}
                              renderOption={(props, option) => (
                                <li {...props} key={option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Code ? `${option.Code} - ${option.Name}` : option.Name}</li>
                              )}
                              ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Item"
                                  size="small"
                                  InputProps={{ ...params.InputProps, style: inputStyle }}
                                  InputLabelProps={{ style: labelStyle }}
                                />
                              )}
                            />
                            <TextField
                              size="small"
                              type="number"
                              label="Qty"
                              value={productionQuantity}
                              onChange={(e) => setProductionQuantity(e.target.value)}
                              style={{ width: '90px' }}
                              inputProps={{ min: 0.01, step: 0.01, style: { padding: '6px 8px', fontSize: '12px' } }}
                              InputLabelProps={{ style: labelStyle }}
                            />
                            <TextField
                              size="small"
                              type="number"
                              label="Rate"
                              value={productionRate}
                              onChange={(e) => setProductionRate(e.target.value)}
                              style={{ width: '90px' }}
                              inputProps={{ min: 0, step: 0.01, style: { padding: '6px 8px', fontSize: '12px' } }}
                              InputLabelProps={{ style: labelStyle }}
                            />
                            <Tooltip title="Add Item to Production">
                              <span>
                                <IconButton
                                  onClick={handleAddProductionItem}
                                  disabled={!selectedProductionItem || !productionQuantity || parseFloat(productionQuantity) <= 0 || productionLoading}
                                  size="small"
                                  style={{
                                    background: selectedProductionItem && productionQuantity && parseFloat(productionQuantity) > 0 && !productionLoading
                                      ? 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)'
                                      : '#e0e0e0',
                                    color: 'white',
                                    borderRadius: '6px',
                                    padding: '6px'
                                  }}
                                >
                                  <AddCircleOutlineIcon style={{ fontSize: '16px' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Grid>
                      )}

                      {/* Production items table */}
                      <Grid item xs={12}>
                        {productionLoading ? (
                          <Box style={{ textAlign: 'center', padding: '16px', color: '#888', fontSize: '12px', border: '1px solid #e9ecef', borderRadius: '6px' }}>Loading...</Box>
                        ) : (
                          <Box style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                            <Table size="small" style={{ tableLayout: 'fixed' }}>
                              <TableHead>
                                <TableRow style={{ background: '#f1f5f9' }}>
                                  <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '8%' }}>#</TableCell>
                                  <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '40%' }}>Item Name</TableCell>
                                  <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '20%' }}>Quantity</TableCell>
                                  <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '20%' }}>Rate</TableCell>
                                  {!isReadOnly && (
                                    <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '12%', textAlign: 'center' }}>Action</TableCell>
                                  )}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {productionItems.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={isReadOnly ? 4 : 5} style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '10px' }}>
                                      No production items added
                                    </TableCell>
                                  </TableRow>
                                ) : productionItems.map((prod, index) => (
                                  <TableRow key={prod.Id || prod._itemId || prod.ItemId} style={{ background: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                                    <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#555' }}>{index + 1}</TableCell>
                                    <TableCell style={{ fontSize: '11px', padding: '4px 8px', fontWeight: '500' }}>{prod.ItemCode ? `${prod.ItemCode} - ${prod.ItemName}` : prod.ItemName}</TableCell>
                                    <TableCell style={{ padding: '4px 8px' }}>
                                      {isReadOnly ? (
                                        <span style={{ fontSize: '11px' }}>{prod.Quantity}</span>
                                      ) : (
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={prod.Quantity}
                                          onChange={(e) => handleProductionFieldChange(prod, 'Quantity', e.target.value)}
                                          inputProps={{ min: 0.01, step: 0.01, style: { padding: '3px 6px', fontSize: '11px' } }}
                                          style={{ width: '80px' }}
                                          sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' } }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell style={{ padding: '4px 8px' }}>
                                      {isReadOnly ? (
                                        <span style={{ fontSize: '11px' }}>{prod.Rate}</span>
                                      ) : (
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={prod.Rate}
                                          onChange={(e) => handleProductionFieldChange(prod, 'Rate', e.target.value)}
                                          inputProps={{ min: 0, step: 0.01, style: { padding: '3px 6px', fontSize: '11px' } }}
                                          style={{ width: '80px' }}
                                          sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' } }}
                                        />
                                      )}
                                    </TableCell>
                                    {!isReadOnly && (
                                      <TableCell style={{ padding: '4px 8px', textAlign: 'center' }}>
                                        <Tooltip title="Remove">
                                          <IconButton size="small" onClick={() => handleRemoveProductionItem(prod)} style={{ color: '#e53935', padding: '2px' }}>
                                            <DeleteOutlineIcon style={{ fontSize: '14px' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Opening Balances Section */}
              <Grid item xs={12}>
                <Box
                  onClick={() => setOpeningBalanceExpanded(prev => !prev)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginTop: '4px'
                  }}
                >
                  <Typography variant="subtitle1" style={{
                    fontWeight: '600',
                    color: '#764ba2',
                    fontSize: '13px'
                  }}>
                    Opening Balance (Branch-wise)
                  </Typography>
                  <IconButton size="small" style={{ padding: '2px' }}>
                    {openingBalanceExpanded ? (
                      <ExpandLessIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                    ) : (
                      <ExpandMoreIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                    )}
                  </IconButton>
                </Box>
              </Grid>
              {openingBalanceExpanded && (
                <Grid item xs={12}>
                  {branchesLoading ? (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                      <CircularProgress size={14} />
                      <span style={{ fontSize: '12px', color: '#64748b' }}>Loading branches...</span>
                    </Box>
                  ) : (
                    <Box style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                      <Table size="small" style={{ tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow style={{ background: '#f1f5f9' }}>
                            <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '35%' }}>Branch</TableCell>
                            <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '35%' }}>Opening Qty</TableCell>
                            <TableCell style={{ fontSize: '10px', fontWeight: 700, color: '#1e3a8a', padding: '5px 8px', width: '30%' }}>Opening Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(formData.OpeningBalances || []).map((row, idx) => (
                            <TableRow key={row.BranchId} style={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                              <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#374151', fontWeight: 500 }}>
                                {row.BranchName}
                              </TableCell>
                              <TableCell style={{ padding: '4px 8px' }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={row.OpeningBalance ?? 0}
                                  onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningBalance', parseFloat(e.target.value) || 0)}
                                  disabled={isReadOnly}
                                  inputProps={{ min: 0, style: { padding: '3px 6px', fontSize: '11px' } }}
                                  style={{ width: '100%' }}
                                  sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' } }}
                                />
                              </TableCell>
                              <TableCell style={{ padding: '4px 8px' }}>
                                <TextField
                                  size="small"
                                  type="date"
                                  value={row.OpeningDate?.split('T')[0] || ''}
                                  onChange={(e) => handleOpeningBalanceChange(row.BranchId, 'OpeningDate', e.target.value)}
                                  disabled={isReadOnly}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ style: { padding: '3px 6px', fontSize: '11px' } }}
                                  style={{ width: '100%' }}
                                  sx={{ '& .MuiOutlinedInput-root': { minHeight: '26px' } }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {(formData.OpeningBalances || []).length === 0 && (
                            <TableRow>
                              <TableCell colSpan={3} style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '10px' }}>
                                No branches available
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Grid>
              )}

              {/* Supplier / Vendor Section - View mode only */}
              {isReadOnly && (
                <>
                  <Grid item xs={12}>
                    <Box
                      onClick={() => setVendorsExpanded(prev => !prev)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        marginTop: '4px'
                      }}
                    >
                      <Typography variant="subtitle1" style={{
                        fontWeight: '600',
                        color: '#764ba2',
                        fontSize: '13px'
                      }}>
                        Suppliers / Vendors
                      </Typography>
                      <IconButton size="small" style={{ padding: '2px' }}>
                        {vendorsExpanded ? (
                          <ExpandLessIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                        ) : (
                          <ExpandMoreIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                        )}
                      </IconButton>
                    </Box>
                  </Grid>

                  {vendorsExpanded && (
                    <>
                      {/* Add vendor row */}
                      <Grid item xs={12}>
                        <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <Autocomplete
                            key={vendorDropdownKey}
                            size="small"
                            options={vendors.filter(v => !itemVendors.some(iv => (iv.MasterId || iv._masterId) === (v.MasterId || v.Id)))}
                            getOptionLabel={(option) => option.Name || ''}
                            isOptionEqualToValue={(option, value) => (option.MasterId || option.Id) === (value.MasterId || value.Id)}
                            value={selectedVendor}
                            onChange={(_, newVal) => setSelectedVendor(newVal)}
                            style={{ flex: 1 }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.MasterId || option.Id} style={{ fontSize: '11px', padding: '4px 8px' }}>{option.Name}</li>
                            )}
                            ListboxProps={{ style: { maxHeight: '200px', fontSize: '11px' } }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Supplier / Vendor"
                                size="small"
                                InputProps={{ ...params.InputProps, style: inputStyle }}
                                InputLabelProps={{ style: labelStyle }}
                              />
                            )}
                          />
                          <Tooltip title="Add Vendor">
                            <span>
                              <IconButton
                                onClick={handleAddVendor}
                                disabled={!selectedVendor || vendorLoading}
                                size="small"
                                style={{
                                  background: selectedVendor && !vendorLoading
                                    ? 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)'
                                    : '#e0e0e0',
                                  color: 'white',
                                  borderRadius: '6px',
                                  padding: '6px'
                                }}
                              >
                                <PersonAddIcon style={{ fontSize: '16px' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Grid>

                      {/* 2-column vendor tables */}
                      <Grid item xs={12}>
                        <Box style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>

                          {/* Left — Active Vendors */}
                          <Box style={{ flex: 1 }}>
                            <Typography style={{ fontSize: '11px', fontWeight: '700', color: '#45a359', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Active Vendors
                            </Typography>
                            {vendorLoading ? (
                              <Box style={{ textAlign: 'center', padding: '16px', color: '#888', fontSize: '12px', border: '1px solid #e9ecef', borderRadius: '6px' }}>Loading...</Box>
                            ) : (
                              <Table size="small" style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                                <TableHead>
                                  <TableRow style={{ background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)' }}>
                                    <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600' }}>#</TableCell>
                                    <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600' }}>Name</TableCell>
                                    <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600', textAlign: 'center' }}>Del</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {itemVendors.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={3} style={{ textAlign: 'center', fontSize: '11px', color: '#aaa', padding: '10px' }}>No active vendors</TableCell>
                                    </TableRow>
                                  ) : itemVendors.map((vendor, index) => (
                                    <TableRow key={vendor.Id || vendor._masterId} style={{ background: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                                      <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#555' }}>{index + 1}</TableCell>
                                      <TableCell style={{ fontSize: '11px', padding: '4px 8px', fontWeight: '500' }}>{vendor.MasterName || vendor.Name}</TableCell>
                                      <TableCell style={{ padding: '4px 8px', textAlign: 'center' }}>
                                        <Tooltip title="Remove">
                                          <IconButton size="small" onClick={() => handleRemoveVendor(vendor)} style={{ color: '#e53935', padding: '2px' }}>
                                            <DeleteOutlineIcon style={{ fontSize: '14px' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>

                          {/* Right — Inactive Vendors */}
                          <Box style={{ flex: 1 }}>
                            <Typography style={{ fontSize: '11px', fontWeight: '700', color: '#e53935', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Inactive Vendors
                            </Typography>
                            {inactiveVendorLoading ? (
                              <Box style={{ textAlign: 'center', padding: '16px', color: '#888', fontSize: '12px', border: '1px solid #e9ecef', borderRadius: '6px' }}>Loading...</Box>
                            ) : (
                              <Table size="small" style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                                <TableHead>
                                  <TableRow style={{ background: 'linear-gradient(135deg, #555 0%, #333 100%)' }}>
                                    <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600' }}>#</TableCell>
                                    <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600' }}>Name</TableCell>
                                    <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600', textAlign: 'center' }}>Activate</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {inactiveVendors.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={3} style={{ textAlign: 'center', fontSize: '11px', color: '#aaa', padding: '10px' }}>No inactive vendors</TableCell>
                                    </TableRow>
                                  ) : inactiveVendors.map((vendor, index) => (
                                    <TableRow key={vendor.Id} style={{ background: index % 2 === 0 ? '#fff5f5' : 'white' }}>
                                      <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#555' }}>{index + 1}</TableCell>
                                      <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#888' }}>{vendor.MasterName || vendor.Name}</TableCell>
                                      <TableCell style={{ padding: '4px 8px', textAlign: 'center' }}>
                                        <Tooltip title="Activate">
                                          <IconButton
                                            size="small"
                                            onClick={() => { setVendorToActivate(vendor); setVendorActivateConfirm(true); }}
                                            style={{ color: '#45a359', padding: '2px' }}
                                          >
                                            <CheckCircleOutlineIcon style={{ fontSize: '14px' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>

                        </Box>
                      </Grid>

                      {/* Submit vendors button */}
                      {itemVendors.some(v => v._isNew) && (
                        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                          <Button
                            onClick={handleSubmitVendors}
                            disabled={vendorLoading}
                            variant="contained"
                            size="small"
                            style={{
                              background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
                              color: 'white',
                              textTransform: 'none',
                              fontWeight: '600',
                              borderRadius: '6px',
                              padding: '5px 14px',
                              fontSize: '12px'
                            }}
                          >
                            {vendorLoading ? 'Saving...' : 'Submit Suppliers'}
                          </Button>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Item Specs Section */}
              <Grid item xs={12}>
                <Box
                  onClick={() => setSpecsExpanded(prev => !prev)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginTop: '4px'
                  }}
                >
                  <Typography variant="subtitle1" style={{ fontWeight: '600', color: '#1e40af', fontSize: '13px' }}>
                    Item Specifications
                  </Typography>
                  <IconButton size="small" style={{ padding: '2px' }}>
                    {specsExpanded
                      ? <ExpandLessIcon style={{ fontSize: '18px', color: '#1e40af' }} />
                      : <ExpandMoreIcon style={{ fontSize: '18px', color: '#1e40af' }} />}
                  </IconButton>
                </Box>
              </Grid>

              {specsExpanded && (
                <>
                  {/* Add spec row */}
                  {!isReadOnly && (
                    <Grid item xs={12}>
                      <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <TextField
                          size="small"
                          label="Specification"
                          value={newSpecText}
                          onChange={(e) => setNewSpecText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSpec(); } }}
                          style={{ flex: 1 }}
                          inputProps={{ maxLength: 150 }}
                          InputProps={{ style: inputStyle }}
                          InputLabelProps={{ style: labelStyle }}
                        />
                        <Tooltip title="Add Spec">
                          <span>
                            <IconButton
                              onClick={handleAddSpec}
                              disabled={!newSpecText.trim() || specsLoading}
                              size="small"
                              style={{
                                background: newSpecText.trim() && !specsLoading
                                  ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)'
                                  : '#e0e0e0',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '6px'
                              }}
                            >
                              <AddCircleOutlineIcon style={{ fontSize: '16px' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Grid>
                  )}

                  {/* Specs table */}
                  <Grid item xs={12}>
                    {specsLoading ? (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Loading...</span>
                      </Box>
                    ) : (
                      <Box style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                        <Table size="small" style={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow style={{ background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)' }}>
                              <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600', width: '40px' }}>#</TableCell>
                              <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600' }}>Specification</TableCell>
                              {!isReadOnly && (
                                <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600', textAlign: 'center', width: '48px' }}>Del</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {itemSpecs.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={isReadOnly ? 2 : 3} style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '12px' }}>
                                  No specifications added
                                </TableCell>
                              </TableRow>
                            ) : itemSpecs.map((spec, index) => (
                              <TableRow key={spec.Id || spec._tempId} style={{ background: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                                <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#555' }}>{index + 1}</TableCell>
                                <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#374151' }}>{spec.Spec}</TableCell>
                                {!isReadOnly && (
                                  <TableCell style={{ padding: '4px 8px', textAlign: 'center' }}>
                                    <Tooltip title="Remove">
                                      <IconButton size="small" onClick={() => handleRemoveSpec(spec)} style={{ color: '#e53935', padding: '2px' }}>
                                        <DeleteOutlineIcon style={{ fontSize: '14px' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Grid>
                </>
              )}

              {/* Item Offers & Warranty Section */}
              <Grid item xs={12}>
                <Box
                  onClick={() => setOffersExpanded(prev => !prev)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginTop: '4px'
                  }}
                >
                  <Typography variant="subtitle1" style={{ fontWeight: '600', color: '#764ba2', fontSize: '13px' }}>
                    Item Offers &amp; Warranty Details
                  </Typography>
                  <IconButton size="small" style={{ padding: '2px' }}>
                    {offersExpanded
                      ? <ExpandLessIcon style={{ fontSize: '18px', color: '#764ba2' }} />
                      : <ExpandMoreIcon style={{ fontSize: '18px', color: '#764ba2' }} />}
                  </IconButton>
                </Box>
              </Grid>

              {offersExpanded && (
                <>
                  {/* Add offer row */}
                  {!isReadOnly && (
                    <Grid item xs={12}>
                      <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <TextField
                          size="small"
                          label="Offer / Warranty Description"
                          value={newOfferText}
                          onChange={(e) => setNewOfferText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOffer(); } }}
                          style={{ flex: 1 }}
                          inputProps={{ maxLength: 250 }}
                          InputProps={{ style: inputStyle }}
                          InputLabelProps={{ style: labelStyle }}
                        />
                        <Tooltip title="Add">
                          <span>
                            <IconButton
                              onClick={handleAddOffer}
                              disabled={!newOfferText.trim() || offersLoading}
                              size="small"
                              style={{
                                background: newOfferText.trim() && !offersLoading
                                  ? 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)'
                                  : '#e0e0e0',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '6px'
                              }}
                            >
                              <AddCircleOutlineIcon style={{ fontSize: '16px' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Grid>
                  )}

                  {/* Offers table */}
                  <Grid item xs={12}>
                    {offersLoading ? (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                        <CircularProgress size={14} />
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Loading...</span>
                      </Box>
                    ) : (
                      <Box style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                        <Table size="small" style={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow style={{ background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)' }}>
                              <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600', width: '40px' }}>#</TableCell>
                              <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600' }}>Description</TableCell>
                              {!isReadOnly && (
                                <TableCell style={{ color: 'white', fontSize: '10px', padding: '5px 8px', fontWeight: '600', textAlign: 'center', width: '48px' }}>Del</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {offersAndWarranty.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={isReadOnly ? 2 : 3} style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '12px' }}>
                                  No offers or warranty details added
                                </TableCell>
                              </TableRow>
                            ) : offersAndWarranty.map((offer, index) => (
                              <TableRow key={offer.Id || offer._tempId} style={{ background: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                                <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#555' }}>{index + 1}</TableCell>
                                <TableCell style={{ fontSize: '11px', padding: '4px 8px', color: '#374151' }}>{offer.OffersAndWarranty}</TableCell>
                                {!isReadOnly && (
                                  <TableCell style={{ padding: '4px 8px', textAlign: 'center' }}>
                                    <Tooltip title="Remove">
                                      <IconButton size="small" onClick={() => handleRemoveOffer(offer)} style={{ color: '#e53935', padding: '2px' }}>
                                        <DeleteOutlineIcon style={{ fontSize: '14px' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>

          <DialogActions style={{
            padding: '8px 12px',
            background: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            gap: '6px',
            flexShrink: 0
          }}>
            <Button
              onClick={onClose}
              style={{
                color: '#6c757d',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '12px',
                padding: '4px 8px'
              }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  fontSize: '12px'
                }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Add Item' : 'Update Item'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon
            style={{
              fontSize: '64px',
              color: '#ff9800',
              marginBottom: '16px'
            }}
          />
          <Typography
            variant="h6"
            style={{
              fontWeight: '600',
              marginBottom: '12px',
              color: '#2d3748',
              fontSize: '16px'
            }}
          >
            Duplicate Item Found
          </Typography>
          <Typography
            style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            {duplicateMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '8px',
              padding: '8px 32px',
              fontSize: '14px'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor Delete Confirmation Dialog */}
      <Dialog
        open={vendorDeleteConfirm}
        onClose={() => { setVendorDeleteConfirm(false); setVendorToDelete(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '56px', color: '#ff9800', marginBottom: '12px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748', fontSize: '15px' }}>
            Remove Supplier?
          </Typography>
          <Typography style={{ color: '#666', fontSize: '13px' }}>
            Are you sure you want to remove <strong>{vendorToDelete?.MasterName}</strong> from this item?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px 24px', justifyContent: 'center', gap: '8px' }}>
          <Button
            onClick={() => { setVendorDeleteConfirm(false); setVendorToDelete(null); }}
            style={{ background: '#e0e0e0', color: '#555', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteVendor}
            disabled={vendorLoading}
            variant="contained"
            style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            {vendorLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Vendor Activate Confirmation Dialog */}
      <Dialog
        open={vendorActivateConfirm}
        onClose={() => { setVendorActivateConfirm(false); setVendorToActivate(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <CheckCircleOutlineIcon style={{ fontSize: '56px', color: '#45a359', marginBottom: '12px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748', fontSize: '15px' }}>
            Activate Supplier?
          </Typography>
          <Typography style={{ color: '#666', fontSize: '13px' }}>
            Are you sure you want to activate <strong>{vendorToActivate?.MasterName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px 24px', justifyContent: 'center', gap: '8px' }}>
          <Button
            onClick={() => { setVendorActivateConfirm(false); setVendorToActivate(null); }}
            style={{ background: '#e0e0e0', color: '#555', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmActivateVendor}
            disabled={inactiveVendorLoading}
            variant="contained"
            style={{ background: 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            {inactiveVendorLoading ? 'Activating...' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Item Delete Confirmation Dialog */}
      <Dialog
        open={bundleDeleteConfirm}
        onClose={() => { setBundleDeleteConfirm(false); setBundleToDelete(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '56px', color: '#ff9800', marginBottom: '12px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748', fontSize: '15px' }}>
            Remove Bundle Item?
          </Typography>
          <Typography style={{ color: '#666', fontSize: '13px' }}>
            Are you sure you want to remove <strong>{bundleToDelete?.ItemName}</strong> from this bundle?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px 24px', justifyContent: 'center', gap: '8px' }}>
          <Button
            onClick={() => { setBundleDeleteConfirm(false); setBundleToDelete(null); }}
            style={{ background: '#e0e0e0', color: '#555', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteBundleItem}
            disabled={bundleLoading}
            variant="contained"
            style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            {bundleLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Production Item Delete Confirmation Dialog */}
      <Dialog
        open={productionDeleteConfirm}
        onClose={() => { setProductionDeleteConfirm(false); setProductionToDelete(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '56px', color: '#ff9800', marginBottom: '12px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748', fontSize: '15px' }}>
            Remove Production Item?
          </Typography>
          <Typography style={{ color: '#666', fontSize: '13px' }}>
            Are you sure you want to remove <strong>{productionToDelete?.ItemName}</strong> from this production?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px 24px', justifyContent: 'center', gap: '8px' }}>
          <Button
            onClick={() => { setProductionDeleteConfirm(false); setProductionToDelete(null); }}
            style={{ background: '#e0e0e0', color: '#555', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteProductionItem}
            disabled={productionLoading}
            variant="contained"
            style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            {productionLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offer & Warranty Delete Confirmation Dialog */}
      <Dialog
        open={offerDeleteConfirm}
        onClose={() => { setOfferDeleteConfirm(false); setOfferToDelete(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '56px', color: '#ff9800', marginBottom: '12px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748', fontSize: '15px' }}>
            Remove Offer / Warranty?
          </Typography>
          <Typography style={{ color: '#666', fontSize: '13px' }}>
            Are you sure you want to remove this entry: <strong>"{offerToDelete?.OffersAndWarranty}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px 24px', justifyContent: 'center', gap: '8px' }}>
          <Button
            onClick={() => { setOfferDeleteConfirm(false); setOfferToDelete(null); }}
            style={{ background: '#e0e0e0', color: '#555', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteOffer}
            disabled={offersLoading}
            variant="contained"
            style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            {offersLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Spec Delete Confirmation Dialog */}
      <Dialog
        open={specDeleteConfirm}
        onClose={() => { setSpecDeleteConfirm(false); setSpecToDelete(null); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '56px', color: '#ff9800', marginBottom: '12px' }} />
          <Typography variant="h6" style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748', fontSize: '15px' }}>
            Remove Specification?
          </Typography>
          <Typography style={{ color: '#666', fontSize: '13px' }}>
            Are you sure you want to remove this spec: <strong>"{specToDelete?.Spec}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 20px 24px', justifyContent: 'center', gap: '8px' }}>
          <Button
            onClick={() => { setSpecDeleteConfirm(false); setSpecToDelete(null); }}
            style={{ background: '#e0e0e0', color: '#555', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSpec}
            disabled={specsLoading}
            variant="contained"
            style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '8px', padding: '6px 20px', fontSize: '13px' }}
          >
            {specsLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemModal;
